/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { AnalyticsConstants, Diagnostics, Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { AiringOffer } from 'client-code/airing';

import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { DeviceModel } from '../device/DeviceModel';
import { Channel } from '../entities/Channel';
import { GuideCell } from '../entities/GuideCell';
import { GuideError, SignOutError } from '../error';
import { PreSignInError } from '../error/PreSignInError';
import { SignInError } from '../error/SignInError';
import { Keys } from '../navigation/Keys';
import { Storage } from '../storage/Storage';
import { TVuLoggerProperties } from '../types';
import { PlaybackRestrictionType } from '../videoplayer/enums';
import { IVULiveTvPayload } from './IVULiveTvPayload';
import { Log } from './Log';

export type ErrorEventData = {
    code: string;
    message: string;
    source: string;
    type: string;
};

export type UserActionEventData = {
    actionName: string;
    action: string;
    screen: string;
    selection: string;
};

export class VuLogger {
    private static TAG = 'VuLogger';

    private static globalParams: Payload;

    private static guideEntryTime: number = Date.now();

    // flag to check that watchStopped event should only log if watchStarted
    private static isWatchStarted: boolean = false;

    // flag to check sessionId should be present in watchStartedEvent
    private static logWatchStartedEventFlag: boolean = false;

    private static mUserId: string;

    private static sessionId: string | undefined = undefined;

    private static signinEntryTime: number = Date.now();

    // setting playUrl for errorEvent
    public static playUrl: string | undefined = undefined;

    // flag to log streaming recovery if session error recovers
    public static sessionErrorRecoveryFlag: boolean = false;

    // flag to log streaming recovery only when player recovers
    public static streamingRecoveryFlag: boolean = false;

    public static ErrorEventSource = {
        STREAMING_SESSION_ERROR: 'streamingSessionError',
    };

    private static LiveTvPayloadDefaultValues: IVULiveTvPayload = {
        callSign: undefined,
        channelName: undefined,
        duration: undefined,
        episodeNumber: undefined,
        episodeTitle: undefined,
        genre: undefined,
        seasonNumber: undefined,
        sessionId: undefined,
        startTime: undefined,
        stationId: undefined,
        viewingSessionId: undefined,
        year: undefined,
    };

    public static NotifyEventSourceType = {
        ADULT_OSD: 'AdultOSD',
        BLACKLISTED_OSD: 'BlacklistedOSD',
        CHANNEL_OPTIONS_OVERLAY: 'ChannelOptionsOverlay',
        DEVICE_TYPE_RESTRICTED_OSD: 'DeviceTypeRestricted',
        ERROR_OVERLAY: 'ErrorOverlay',
        EXIT_CONFIRMATION_OVERLAY: 'ExitConfirmationOverlay',
        IN_HOME_LOCATION_OSD: 'InHomeLocationOSD',
        NO_NETWORK_CONNECTION_OSD: 'NoNetworkConnectionOSD',
        NO_NETWORK_CONNECTION_OVERLAY: 'NoNetworkConnectionOverlay',
        OUT_OF_HOME_OSD: 'OutOfHomeOSD',
        OUT_OF_REGION_OSD: 'OutOfRegionOSD',
        STILL_THERE_OSD: 'StillThereOSD',
        TEMPORARY_GUIDE_ISSUE_OVERLAY: 'TemporaryGuideIssueOverlay',
        UNSUBSCRIBED_OSD: 'UnsubscribedOSD',
    };

    public static NotifyEventSourceName = {
        ADULT_SHOW: 'Adult Show',
        BLACKLISTED: 'Blacklisted',
        CHANNEL_OPTIONS: 'Channel Options',
        DEVICE_TYPE_RESTRICTED: 'Device Type Restricted',
        EXIT_CONFIRMATION: 'Exit Kaveri?',
        IN_HOME_LOCATION: 'In Home Location',
        NO_NETWORK_CONNECTION: 'No Network Connection',
        OUT_OF_HOME: 'Out Of Home',
        OUT_OF_REGION: 'Out Of Region',
        PLAYER_ERROR: 'Player Error',
        SESSION_ERROR: 'Session Error',
        STILL_THERE: 'Still There',
        TEMPORARY_GUIDE_ISSUE: 'Temporary Guide Issue',
        UNSUBSCRIBED_CHANNEL: 'Unsubscribed Channel',
    };

    private static OverlayNotificationEvents = {
        [OverlayTypes.CHANNEL_OPTIONS]: {
            sourceType: VuLogger.NotifyEventSourceType.CHANNEL_OPTIONS_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.CHANNEL_OPTIONS,
        },
        [OverlayTypes.EXIT_CONFIRMATION]: {
            sourceType: VuLogger.NotifyEventSourceType.EXIT_CONFIRMATION_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.EXIT_CONFIRMATION,
        },
        [OverlayTypes.NO_NETWORK]: {
            sourceType: VuLogger.NotifyEventSourceType.NO_NETWORK_CONNECTION_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.NO_NETWORK_CONNECTION,
        },
        [OverlayTypes.TEMPORARY_GUIDE_ISSUE]: {
            sourceType: VuLogger.NotifyEventSourceType.TEMPORARY_GUIDE_ISSUE_OVERLAY,
            sourceName: VuLogger.NotifyEventSourceName.TEMPORARY_GUIDE_ISSUE,
        },
    };

    private static RestrictionNotificationEvents = {
        [PlaybackRestrictionType.ADULT]: {
            sourceType: VuLogger.NotifyEventSourceType.ADULT_OSD,
            sourceName: VuLogger.NotifyEventSourceName.ADULT_SHOW,
        },
        [PlaybackRestrictionType.BLACKLISTED]: {
            sourceType: VuLogger.NotifyEventSourceType.BLACKLISTED_OSD,
            sourceName: VuLogger.NotifyEventSourceName.BLACKLISTED,
        },
        [PlaybackRestrictionType.DEVICE_RESTRICTED]: {
            sourceType: VuLogger.NotifyEventSourceType.DEVICE_TYPE_RESTRICTED_OSD,
            sourceName: VuLogger.NotifyEventSourceName.DEVICE_TYPE_RESTRICTED,
        },
        [PlaybackRestrictionType.OUT_OF_HOME]: {
            sourceType: VuLogger.NotifyEventSourceType.OUT_OF_HOME_OSD,
            sourceName: VuLogger.NotifyEventSourceName.OUT_OF_HOME,
        },
        [PlaybackRestrictionType.NETWORK_DISCONNECTED]: {
            sourceType: VuLogger.NotifyEventSourceType.NO_NETWORK_CONNECTION_OSD,
            sourceName: VuLogger.NotifyEventSourceName.NO_NETWORK_CONNECTION,
        },
        [PlaybackRestrictionType.OUT_OF_REGION]: {
            sourceType: VuLogger.NotifyEventSourceType.OUT_OF_REGION_OSD,
            sourceName: VuLogger.NotifyEventSourceName.OUT_OF_REGION,
        },
        [PlaybackRestrictionType.UNSUBSCRIBED]: {
            sourceType: VuLogger.NotifyEventSourceType.UNSUBSCRIBED_OSD,
            sourceName: VuLogger.NotifyEventSourceName.UNSUBSCRIBED_CHANNEL,
        },
        [PlaybackRestrictionType.INHOME_LOCATION_ERROR]: {
            sourceType: VuLogger.NotifyEventSourceType.IN_HOME_LOCATION_OSD,
            sourceName: VuLogger.NotifyEventSourceName.IN_HOME_LOCATION,
        },
    };

    public static StreamingRecoveryType = {
        NETWORK_ERROR: 'NETWORK_ERROR',
        PLAYBACK_ERROR: 'PLAYBACK_ERROR',
        SESSION_ERROR: 'SESSION_ERROR',
    };

    public static TrickplayMode = {
        FAST_FORWARD_1: 'FAST_FORWARD_1',
        FAST_FORWARD_2: 'FAST_FORWARD_2',
        FAST_FORWARD_3: 'FAST_FORWARD_3',
        PLAY: 'PLAY',
        PAUSE: 'PAUSE',
        REWIND_1: 'REWIND_1',
        REWIND_2: 'REWIND_2',
        REWIND_3: 'REWIND_3',
    };

    public static UserActionEventActionName = {
        AUDIO_LANGUAGE: 'AUDIO_LANGUAGE',
        CHANNEL_OPTIONS: 'CHANNEL_OPTIONS',
        ERROR_OVERLAY: 'ERROR_OVERLAY',
        EXIT_CONFIRMATION: 'EXIT_CONFIRMATION',
        LANGUAGE_NOT_SUPPORTED: 'LANGUAGE_NOT_SUPPORTED',
        NO_NETWORK: 'NO_NETWORK',
        SUBTITLE_AND_CC: 'SUBTITLE_AND_CC',
        TEMPORARY_GUIDE_ISSUE: 'TEMPORARY_GUIDE_ISSUE',
    };

    public static UserActionEventAction = {
        CANCEL: 'cancel',
        OK: 'ok',
        UPDATE: 'update',
    };

    public static UserActionEventScreen = {
        GUIDE: 'Guide',
        LIVE_TV: 'Live TV',
        OVERLAYS: 'Overlays',
    };

    public static UserSelection = {
        OK: 'Ok',
        CANCEL: 'Cancel',
    };

    public static WatchStoppedEndReason = {
        CHANNEL_CHANGE: 'CHANNEL_CHANGE',
        END_OF_CONTENT: 'END_OF_CONTENT',
        ENDED_BY_USER: 'ENDED_BY_USER',
        VIDEO_WINDOW_OFF: 'VIDEO_WINDOW_OFF',
        VISIBILITY_BEHIND: 'VISIBILITY_BEHIND',
    };

    public static AppEntryUserTypes = {
        NEW_USER: 'newUser',
        USER_RETURN: 'userReturn',
    };

    public static AppEntryTypes = {
        FIRST_TIME_LAUNCH: 'firstTimeLaunch',
        NONE: 'none',
    };

    public static AppExitActions = {
        EXIT: 'exit',
        SUSPEND: 'suspend',
    };

    private static liveTvCommonPayload: IVULiveTvPayload = {
        ...VuLogger.LiveTvPayloadDefaultValues,
    };

    // method to set SessionId
    public static setSessionId = (sessionId: string) => {
        VuLogger.sessionId = sessionId;
        VuLogger.liveTvCommonPayload.sessionId = VuLogger.sessionId;
        if (VuLogger.logWatchStartedEventFlag) {
            VuLogger.logWatchStartedEvent();
        }
    };

    // clear SessionId at watchStopped Event
    public static clearSessionId = () => {
        VuLogger.sessionId = undefined;
    };

    public static createPayload = (
        guideCell: GuideCell,
        channel: Channel,
        offerPreview: { [key: string]: any },
    ): Payload => {
        const payload: Payload = new Payload();
        const { callSign, name, stationId } = channel;
        const { startTime, duration, movieYear, contentType, title } = guideCell;
        const contentPreview = offerPreview && offerPreview?.contentPreview;
        const categoryLabel = contentPreview && contentPreview?.categoryLabel;
        const episodeNumber = contentPreview && contentPreview?.episodeNumber;
        payload.add('callSign', callSign || '');
        payload.add('name', name || '');
        payload.add('contentType', contentType || '');
        payload.add('duration', duration || '');
        payload.add('episodeTitle', title || undefined);
        payload.add('genre', categoryLabel || '');
        payload.add('startTime', startTime.getTime() || '');
        payload.add('stationId', stationId || '');
        payload.add('year', movieYear || undefined);
        payload.add('episodeNumber', episodeNumber || undefined);
        return payload;
    };

    public static logAppError = (error: GuideError | SignOutError) => {
        const payload: Payload = new Payload();
        const errorPayload = {
            code: error.getCode(),
            message: error.getMessage(),
            source: error.TAG,
            type: error.getType(),
        };
        payload.add('error', errorPayload);
        payload.add('userId', VuLogger.mUserId);
        VU.appError(payload);
    };

    public static logErrorEvent = (error: ErrorEventData) => {
        const payload: Payload = new Payload({ ...VuLogger.liveTvCommonPayload });
        payload.add('error', { ...error, url: VuLogger.playUrl });
        payload.add('userId', VuLogger.mUserId);
        VU.errorEvent(payload);
    };

    public static logIdentifyEvent = (userId: string) => {
        VuLogger.mUserId = userId;
        const payload: Payload = new Payload({ userId: VuLogger.mUserId });
        VU.identify(payload);
    };

    public static logAppEntryEvent = () => {
        const payload: Payload = new Payload();
        let entryType = VuLogger.AppEntryTypes.FIRST_TIME_LAUNCH;
        let userType = VuLogger.AppEntryUserTypes.NEW_USER;

        if (Storage.getInstance()?.getItem(Storage.Keys.IS_APP_ALREADY_LAUNCHED)) {
            entryType = VuLogger.AppEntryTypes.NONE;
            userType = VuLogger.AppEntryUserTypes.USER_RETURN;
        } else Storage.getInstance()?.setItem(Storage.Keys.IS_APP_ALREADY_LAUNCHED, true);
        payload.add('entryType', entryType);
        payload.add('userType', userType);
        payload.add('userId', VuLogger.mUserId);
        VU.appEntryEvent(payload);
    };

    public static logAppExitEvent = (action: string) => {
        const payload: Payload = new Payload({ action });
        payload.add('userId', VuLogger.mUserId);
        VU.appExitEvent(payload);
    };

    public static logGuideScreenEntryEvent = () => {
        VuLogger.guideEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    public static logGuideScreenExitEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.EXIT,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.guideEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    public static logGuideScreenPauseEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.SUSPEND,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.guideEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    public static logGuideScreenResumeEvent = () => {
        VuLogger.guideEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, AnalyticsConstants.Screens.GUIDE_SCREEN);
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.RESUME,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    public static logLoginEvent = (msoServiceId: string) => {
        const payload: Payload = new Payload();
        payload.add('msoServiceId', msoServiceId);
        payload.add('userId', VuLogger.mUserId);
        VU.loginEvent(payload);
    };

    public static logLoginFailureEvent = (error: SignInError | PreSignInError) => {
        const payload: Payload = new Payload();
        const errorPayload = {
            code: error.getCode(),
            message: error.getMessage(),
            source: error.TAG,
            type: error.getType(),
        };
        payload.add('userId', VuLogger.mUserId);
        payload.add('error', errorPayload);
        VU.loginFailureEvent(payload);
    };

    public static logNotifyEvent = (sourceType: string, sourceName: string) => {
        const notifyEventPayload = new Payload();
        notifyEventPayload.add('sourceType', sourceType);
        notifyEventPayload.add('sourceName', sourceName);
        notifyEventPayload.add('userId', VuLogger.mUserId);
        VU.notifyEvent(notifyEventPayload);
    };

    public static logGuideNotificationEvent = (overlayType: number) => {
        if (overlayType in VuLogger.OverlayNotificationEvents) {
            const { sourceType, sourceName } = VuLogger.OverlayNotificationEvents[overlayType];
            VuLogger.logNotifyEvent(sourceType, sourceName);
        }
    };

    public static logItemSelectedEvent(properties: TVuLoggerProperties) {
        const { guideCell, channel, offerPreview = {} } = properties;
        const payload = VuLogger.createPayload(guideCell, channel, offerPreview);
        VU.itemSelectedEvent(payload);
    }

    public static logVideoPlayerRestrictionNotificationEvent = (
        restriction: PlaybackRestrictionType,
    ) => {
        if (restriction in VuLogger.RestrictionNotificationEvents) {
            const { sourceType, sourceName } = VuLogger.RestrictionNotificationEvents[restriction];
            VuLogger.logNotifyEvent(sourceType, sourceName);
        }
    };

    public static logKeyEvent = (key: Keys) => {
        const keyEventPayload = new Payload();
        keyEventPayload.add('buttonName', key.keyCode);
        keyEventPayload.add('isLongPress', key.type === 'longkeypressedstart');
        keyEventPayload.add('userId', VuLogger.mUserId);
        if (key.type !== 'longkeypressedstop') VU.keyEvent(keyEventPayload);
    };

    public static logSignInScreenEntryEvent = () => {
        VuLogger.signinEntryTime = Date.now();
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, 'signInScreen');
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        payload.add('userId', VuLogger.mUserId);
        VU.screen(payload);
    };

    public static logSignInScreenExitEvent = () => {
        const payload: Payload = new Payload();
        payload.add(AnalyticsConstants.Fields.SCREEN_NAME, 'signInScreen');
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.EXIT,
        );
        payload.add(
            AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
            Date.now() - VuLogger.signinEntryTime,
        );
        payload.add('userId', VuLogger.mUserId);
        VuLogger.signinEntryTime = Date.now();
        VU.screen(payload);
    };

    public static logStreamingRecovery = (recoveryType: string) => {
        // making payload with common payload
        const commonPayload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const streamingRecoveryPayload = commonPayload.merge(commonPayload);
        streamingRecoveryPayload.add('recoveryType', recoveryType);

        // logging streamingRecovery Event
        VU.streamingRecovery(streamingRecoveryPayload);
    };

    public static logTrickPlayEvent = (trickplayMode: string, currentTime: number) => {
        const payload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const trickPlayPayload = payload.merge(payload);

        trickPlayPayload.add('trickplayAction', trickplayMode);
        trickPlayPayload.add('trickplayActionTime', Date.now());
        trickPlayPayload.add('userId', VuLogger.mUserId);
        if (VuLogger.liveTvCommonPayload.startTime && currentTime) {
            const startTime = Math.floor(
                new Date(VuLogger.liveTvCommonPayload.startTime).getTime() /
                    MILLISECONDS_IN_A_SECOND,
            );
            const currentTimeInUnixSeconds = Math.floor(currentTime / MILLISECONDS_IN_A_SECOND);
            if (currentTimeInUnixSeconds >= startTime) {
                trickPlayPayload.add('offset', currentTimeInUnixSeconds - startTime);
            }
        }
        VU.trickPlayEvent(trickPlayPayload);
    };

    public static logUserActionEvent = (data: UserActionEventData) => {
        const userActionPayload: Payload = new Payload();
        userActionPayload.add('actionName', data.actionName);
        userActionPayload.add('action', data.action);
        userActionPayload.add('screen', data.screen);
        userActionPayload.add('selection', data.selection);
        userActionPayload.add('userId', VuLogger.mUserId);
        VU.userActionEvent(userActionPayload);
    };

    private static logUserActionEventForGuide = (
        actionName: string,
        action: string,
        selection: string,
    ) => {
        VuLogger.logUserActionEvent({
            actionName,
            action,
            screen: VuLogger.UserActionEventScreen.GUIDE,
            selection,
        });
    };

    public static logChannelOptionOverlayOkUserAction = (selectionText: string) => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.CHANNEL_OPTIONS,
            VuLogger.UserActionEventAction.OK,
            selectionText,
        );
    };

    public static logChannelOptionOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.CHANNEL_OPTIONS,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    public static logExitConfirmationOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.EXIT_CONFIRMATION,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    public static logExitConfirmationOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.EXIT_CONFIRMATION,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    public static logLanguageNotSupportedOverlayUserActionEvent = () => {
        VuLogger.logUserActionEvent({
            actionName: VuLogger.UserActionEventActionName.LANGUAGE_NOT_SUPPORTED,
            action: VuLogger.UserActionEventAction.OK,
            screen: VuLogger.UserActionEventScreen.OVERLAYS,
            selection: VuLogger.UserSelection.OK,
        });
    };

    public static logNoNetworkOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.NO_NETWORK,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    public static logNoNetworkOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.NO_NETWORK,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    public static logTemporaryGuideIssueOverlayOkUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.TEMPORARY_GUIDE_ISSUE,
            VuLogger.UserActionEventAction.OK,
            VuLogger.UserSelection.OK,
        );
    };

    public static logTemporaryGuideIssueOverlayCancelUserAction = () => {
        VuLogger.logUserActionEventForGuide(
            VuLogger.UserActionEventActionName.TEMPORARY_GUIDE_ISSUE,
            VuLogger.UserActionEventAction.CANCEL,
            VuLogger.UserSelection.CANCEL,
        );
    };

    public static logWatchStartedEvent = () => {
        // check for sessionId
        if (!VuLogger.sessionId) {
            // setting logWatchStartedEventFlag as sessionId is not present
            VuLogger.logWatchStartedEventFlag = true;

            // setting isWatchStarted as watchStarted did not happen
            VuLogger.isWatchStarted = false;
        } else if (!VuLogger.isWatchStarted) {
            const watchStartedPayload = new Payload(VuLogger.liveTvCommonPayload);
            watchStartedPayload.add('userId', VuLogger.mUserId);
            VU.watchStartedEvent(watchStartedPayload);

            // setting isWatchStarted as watchStarted
            VuLogger.isWatchStarted = true;

            // setting logWatchStartedEventFlag as sessionId is present now
            VuLogger.logWatchStartedEventFlag = false;
        }
    };

    public static logWatchStoppedEvent = (reason: string) => {
        // watchStopped should not log without watchStart
        if (!VuLogger.isWatchStarted) {
            Log.w(
                VuLogger.TAG,
                'Ignored. - Trying to log watchStopped event without watchStarted ...',
            );
            Diagnostics.warn(
                VuLogger.TAG,
                'Ignored. - Trying to log watchStopped event without watchStarted ...',
            );
            return;
        }

        const payload: Payload = new Payload(VuLogger.liveTvCommonPayload);
        const watchStoppedPayload = payload.merge(payload);
        watchStoppedPayload.add('reason', reason);
        watchStoppedPayload.add('userId', VuLogger.mUserId);
        VU.watchStoppedEvent(watchStoppedPayload);

        // setting isWatchStarted as watchStopped is logged
        VuLogger.isWatchStarted = false;

        // Clearing sessionId before next watchStarted
        if (reason !== VuLogger.WatchStoppedEndReason.END_OF_CONTENT) VuLogger.clearSessionId();
    };

    public static setGlobalParams = (globalParams: Payload) => {
        VuLogger.globalParams = globalParams;
        VU.setGlobals(globalParams);
    };

    public static updateGlobalParams = (params: { [key: string]: any }) => {
        const payload = new Payload(params);
        const updatedGlobalPayload = VuLogger.globalParams.merge(payload);
        VuLogger.setGlobalParams(updatedGlobalPayload);
    };

    public static updateLiveTvPayload = (channel: Channel, airingOffer: AiringOffer[]) => {
        VuLogger.liveTvCommonPayload.callSign = channel.callSign;
        VuLogger.liveTvCommonPayload.channelName = channel.name;
        VuLogger.liveTvCommonPayload.duration = airingOffer[0].duration;
        VuLogger.liveTvCommonPayload.episodeNumber = airingOffer[0].banner?.episodeNumber;
        VuLogger.liveTvCommonPayload.episodeTitle = airingOffer[0].banner?.subtitle;
        VuLogger.liveTvCommonPayload.genre = airingOffer[0].logging.genre;
        VuLogger.liveTvCommonPayload.seasonNumber = airingOffer[0].banner?.seasonNumber;
        VuLogger.liveTvCommonPayload.sessionId = VuLogger.sessionId;
        VuLogger.liveTvCommonPayload.startTime = new Date(airingOffer[0].startTime).getTime();
        VuLogger.liveTvCommonPayload.stationId = airingOffer[0].stationId;
        VuLogger.liveTvCommonPayload.viewingSessionId = DeviceModel.generateUniqueId(
            `${Date.now()}`,
        );
        VuLogger.liveTvCommonPayload.year = airingOffer[0].banner?.movieYear;
    };
}
